import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Shield, Users, Wrench, Award, MapPin, Phone, Clock, ChevronRight, MessageCircle } from 'lucide-react';

const values = [
  { icon: Shield, title: 'Transparence', desc: 'Pas de surprise. On vous explique chaque intervention et son coût avant d\'agir.' },
  { icon: Users, title: 'Proximité', desc: 'Un garage de quartier où l\'on connaît chaque client par son nom.' },
  { icon: Wrench, title: 'Expertise', desc: 'Plus de 10 ans d\'expérience sur toutes marques et tous types de véhicules.' },
  { icon: Award, title: 'Qualité', desc: 'Des pièces de qualité, un travail soigné et des véhicules révisés avec soin.' },
];

const About = () => {
  return (
    <div style={{ background: 'var(--bg-primary)', fontFamily: 'var(--font-body)' }} data-testid="about-page">
      {/* Hero - compact */}
      <section className="relative py-16 lg:py-20 overflow-hidden" style={{ background: 'var(--bg-surface)' }}>
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: `url('https://images.pexels.com/photos/3807517/pexels-photo-3807517.jpeg?auto=compress&cs=tinysrgb&w=1920')`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="section-label">À propos</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-3 mb-4 tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              MCA CAR
            </h1>
            <p className="text-zinc-400 max-w-xl">Votre garage de confiance à Quarouble depuis plus de 10 ans.</p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 lg:py-24" style={{ background: 'var(--bg-primary)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="relative">
                <img src="https://images.pexels.com/photos/3807517/pexels-photo-3807517.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Mécanicien MCA CAR au travail" className="w-full h-[350px] lg:h-[400px] object-cover" />
                <div className="absolute -bottom-5 -right-5 bg-[#DC2626] px-5 py-3 hidden lg:block">
                  <p className="text-3xl font-extrabold text-white" style={{ fontFamily: 'var(--font-mono)' }}>10+</p>
                  <p className="text-white/80 text-sm">Ans d'expérience</p>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="section-label">Notre histoire</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mt-3 mb-6 tracking-tight">
                Un garage de proximité<br />au service de votre auto
              </h2>
              <div className="space-y-4 text-zinc-400 text-sm lg:text-base leading-relaxed">
                <p>Situé au c&oelig;ur de Quarouble, <strong className="text-white">MCA CAR</strong> est votre partenaire de confiance pour tous vos besoins automobiles. Notre équipe de mécaniciens qualifiés met son expertise à votre service.</p>
                <p>En plus de notre atelier de réparation, nous proposons une sélection de <strong className="text-white">véhicules d'occasion</strong> soigneusement contrôlés et révisés, ainsi qu'un large choix de pièces détachées de qualité.</p>
                <p>Notre mission : vous offrir un service honnête, transparent et de qualité, au juste prix.</p>
              </div>
              <Link to="/contact" className="group inline-flex items-center gap-2 mt-6 px-6 py-3.5 bg-[#DC2626] text-white font-semibold text-sm transition-all hover:bg-[#B91C1C]" data-testid="about-story-cta">
                Contactez-nous <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 lg:py-24" style={{ background: 'var(--bg-surface)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
            <span className="section-label">Nos valeurs</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mt-3 tracking-tight">Pourquoi nous choisir ?</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {values.map((val, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="border border-white/5 p-6 hover:border-[#DC2626]/30 transition-all duration-300" style={{ background: 'var(--bg-elevated)' }} data-testid={`value-card-${i}`}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white/5 border border-white/10 mb-5">
                  <val.icon size={22} className="text-[#DC2626]" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{val.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{val.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location + CTA */}
      <section className="py-16 lg:py-24" style={{ background: 'var(--bg-primary)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <span className="section-label">Nous trouver</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white mt-3 mb-8 tracking-tight">Venez nous rendre visite</h2>
              <div className="space-y-5">
                {[
                  { icon: MapPin, label: 'Adresse', text: '32 Rue Marcellin Berthelot\n59243 Quarouble' },
                  { icon: Phone, label: 'Téléphone', text: '06 13 85 19 46', href: 'tel:+33613851946' },
                  { icon: Clock, label: 'Horaires', text: 'Lun - Ven: 9h - 12h / 14h - 18h\nSam: Sur rendez-vous' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 flex-shrink-0">
                      <item.icon size={18} className="text-[#DC2626]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm mb-0.5">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-zinc-500 text-sm hover:text-white transition-colors whitespace-pre-line">{item.text}</a>
                      ) : (
                        <p className="text-zinc-500 text-sm whitespace-pre-line">{item.text}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 mt-8">
                <Link to="/contact" className="group inline-flex items-center gap-2 px-6 py-3.5 bg-[#DC2626] text-white font-semibold text-sm transition-all hover:bg-[#B91C1C]" data-testid="about-cta-contact">
                  Nous contacter <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
                <a href="tel:+33613851946" className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/20 text-white font-semibold text-sm transition-all hover:bg-white/5">
                  <Phone size={16} /> Appeler
                </a>
              </div>
            </div>
            <div className="border border-white/5 overflow-hidden h-[350px] lg:h-[400px]">
              <iframe title="MCA CAR - Quarouble" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2539.8!2d3.5988!3d50.3838!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c2e8c0!2s32+Rue+Marcellin+Berthelot+59243+Quarouble!5e0!3m2!1sfr!2sfr!4v1"
                width="100%" height="100%" style={{ border: 0, filter: 'invert(0.9) hue-rotate(180deg) brightness(0.8) contrast(1.2)' }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Sticky Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 flex" style={{ background: 'var(--bg-primary)' }}>
        <a href="tel:+33613851946" className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-[#DC2626] text-white font-semibold text-sm">
          <Phone size={16} /> Appeler
        </a>
        <a href="https://wa.me/33613851946" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-green-600 text-white font-semibold text-sm">
          <MessageCircle size={16} /> WhatsApp
        </a>
      </div>
    </div>
  );
};

export default About;
